import { lazy, Suspense, useEffect, useState } from "react";
import { Router, Route, Switch } from "react-router-dom";
import { createBrowserHistory } from "history";
import StaticRoutes from "./static-pages/StaticRoutes";
import { ApiClientProvider } from "./api/ApiClient";
import "./App.css";
import { TrackPageViews } from "./contract/routing/TrackPageViews";
import { ScrollToTop } from "./contract/routing/ScrollToTop";
import { AppHistoryProvider } from "./contract/routing/AppHistoryProvider";
import { SavedContractsProvider } from "./contract/landing/SavedContracts";
import {
  ContractParamsContext,
  extractContractParams,
} from "./contract/ContractParams";
import { HeaderWithProgressProvider } from "./layout/header/HeaderWithProgress";
import LayoutContext from "./layout/LayoutContext";
import LoadingPage from "./shared/LoadingPage";
import WebpSupport from "./shared/WebpSupport";
import { CreateContractProvider } from "./contract/create-contract/CreateContract";
import { AuthProvider } from "./auth/AuthProvider";
import CustomerProvider from "./auth/CustomerProvider";
import SalesContractApp from "./SalesContractApp";
import { CustomizedThemeProvider as ThemeProvider } from "@teamscheme/theme";
import { GlobalStyle } from "@teamscheme/global-style";
import { AppTrackingProvider } from "./Tracking";
import { ContractUrlsProvider } from "./contract/routing/ContractUrls";
import Main from "./Main";
import CopyFooter from "./shared/elements/CopyFooter";
import { StaticLoadingScreen } from "./shared/LoadingScreen";
import LoginCallbackPage, {
  LogoutCallbackPage,
} from "./auth/LoginCallbackPage";
import { BottomPanelProvider } from "./layout/BottomPanel";
import { WebSocketClientProvider } from "./api/WebSocketClient";

const OidcSignCallbackPage = lazy(() =>
  import("./contract/singing/OidcSignCallbackPage")
);
const NafNoArchiveScreen = lazy(() =>
  import("./contract/confirmation/NafNoArchiveScreen")
);

const SigningTaskPage = lazy(() =>
  import("./contract/signing-task/SigningTaskPage")
);
const SigningTaskCallbackPage = lazy(() =>
  import("./contract/signing-task/SigningTaskCallbackPage")
);

const history = createBrowserHistory();

const params = {
  contract: extractContractParams(document.location.search),
};

const LoadingRouteScreen = () => (
  <div className="App">
    <Main>
      <LoadingPage />
    </Main>
  </div>
);

const onRedirectCallback = (appState) => {
  history.push(
    appState && appState.targetUrl
      ? appState.targetUrl
      : window.location.pathname
  );
};

const AdminRoute = lazy(() => import("./admin/Admin"));
const PaymentRoute = lazy(() => import("./payments/customer/Payment"));

const staticRoutes = Object.values(StaticRoutes).concat([
  {
    path: "/sign/oidc-callback",
    component: OidcSignCallbackPage,
  },
  {
    path: "/login/callback",
    component: LoginCallbackPage,
  },
  {
    path: "/logout/callback",
    component: LogoutCallbackPage,
  },
]);

// Placeholder until we can add /logout/callback to whitelist.
// Must wait for after summer
function useRedirectToLogoutCallback() {
  const { search } = window.location;
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const logout = typeof query.get("logout") === "string";
    if (logout) {
      query.delete("logout");
      window.location.href = `/logout/callback?${query.toString()}`;
    }
  }, [search]);
}

export default function App() {
  const [paddingBottom, setPaddingBottom] = useState(0);
  const [contractParams, setContractParams] = useState(params.contract);
  useRedirectToLogoutCallback();

  return (
    <Router history={history}>
      <Suspense fallback={<LoadingRouteScreen />}>
        <Switch>
          <Route path="/admin" component={AdminRoute} />
          <Route path="/payment" component={PaymentRoute} />
          <Route>
            <ThemeProvider>
              <AppTrackingProvider>
                <GlobalStyle />
                <div
                  className="App"
                  style={{ paddingBottom: `${paddingBottom}px` }}
                >
                  <WebpSupport>
                    <ContractParamsContext.Provider
                      value={{
                        ...contractParams,
                        setParams: setContractParams,
                      }}
                    >
                      <LayoutContext.Provider value={{ setPaddingBottom }}>
                        <AuthProvider onRedirectCallback={onRedirectCallback}>
                          <ContractUrlsProvider>
                            <TrackPageViews>
                              <ScrollToTop>
                                <AppHistoryProvider>
                                  <SavedContractsProvider>
                                    <ApiClientProvider>
                                      <BottomPanelProvider>
                                        <WebSocketClientProvider>
                                          <CustomerProvider>
                                            <CreateContractProvider>
                                              <HeaderWithProgressProvider>
                                                <Route>
                                                  <Suspense
                                                    fallback={
                                                      <StaticLoadingScreen />
                                                    }
                                                  >
                                                    <Switch>
                                                      {[].concat(
                                                        ...staticRoutes.map(
                                                          (route) => (
                                                            <Route
                                                              key={route.path}
                                                              path={route.path}
                                                              component={
                                                                route.component
                                                              }
                                                            />
                                                          )
                                                        )
                                                      )}
                                                      <Route
                                                        path="/signering/:token/callback"
                                                        component={
                                                          SigningTaskCallbackPage
                                                        }
                                                      />
                                                      <Route
                                                        path="/signering/:token"
                                                        component={
                                                          SigningTaskPage
                                                        }
                                                      />
                                                      <Route
                                                        path="/arkiv/naf.no"
                                                        component={
                                                          NafNoArchiveScreen
                                                        }
                                                      />
                                                      <Route
                                                        component={
                                                          SalesContractApp
                                                        }
                                                      />
                                                    </Switch>
                                                  </Suspense>
                                                </Route>
                                              </HeaderWithProgressProvider>
                                            </CreateContractProvider>
                                          </CustomerProvider>
                                        </WebSocketClientProvider>
                                      </BottomPanelProvider>
                                    </ApiClientProvider>
                                  </SavedContractsProvider>
                                </AppHistoryProvider>
                              </ScrollToTop>
                            </TrackPageViews>
                          </ContractUrlsProvider>
                        </AuthProvider>
                      </LayoutContext.Provider>
                    </ContractParamsContext.Provider>
                  </WebpSupport>
                </div>
                <CopyFooter />
              </AppTrackingProvider>
            </ThemeProvider>
          </Route>
        </Switch>
      </Suspense>
    </Router>
  );
}
